import { useCallback, useEffect, useRef, useState } from 'react';
import { toDomainError, type DomainError } from '@domain/errors';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: DomainError | null;
}

export interface AsyncResource<T> extends AsyncState<T> {
  reload: () => void;
  /** Mise à jour optimiste locale sans aller-retour. */
  patch: (updater: (current: T) => T) => void;
}

/**
 * Exécute une requête et gère loading / erreur / annulation.
 * `deps` pilote le rechargement, comme un useEffect.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[]): AsyncResource<T> {
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null });
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const current = ++generation.current;
    setState((previous) => ({ ...previous, loading: true, error: null }));

    loader()
      .then((data) => {
        if (generation.current === current) setState({ data, loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (generation.current === current) {
          setState({ data: null, loading: false, error: toDomainError(error) });
        }
      });

    return () => {
      generation.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  const patch = useCallback((updater: (current: T) => T) => {
    setState((previous) =>
      previous.data === null ? previous : { ...previous, data: updater(previous.data) },
    );
  }, []);

  return { ...state, reload, patch };
}

/** Débounce d'une valeur, utilisé pour les champs de recherche. */
export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Gestion d'une sélection multiple par identifiant. */
export function useSelection(): {
  selected: string[];
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  toggleAll: (ids: readonly string[]) => void;
  allSelected: (ids: readonly string[]) => boolean;
  clear: () => void;
} {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((id: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((ids: readonly string[]) => {
    setSelected((previous) => {
      const everySelected = ids.length > 0 && ids.every((id) => previous.has(id));
      const next = new Set(previous);
      ids.forEach((id) => (everySelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  return {
    selected: [...selected],
    isSelected: (id) => selected.has(id),
    toggle,
    toggleAll,
    allSelected: (ids) => ids.length > 0 && ids.every((id) => selected.has(id)),
    clear,
  };
}
